import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileAPI, authAPI } from '../lib/api';
import { clearAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import AppLayout from '../components/AppLayout';
import ImageUploadField from '../components/ImageUploadField';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';
import { User, Save, Loader2, Download, Trash2, ShieldCheck } from 'lucide-react';

export default function ProfilePage() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone_number: '',
    brand_name: '',
    profile_image: '',
    company_logo: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  // DSGVO Art. 20. Full data export. Backend assembles a ZIP with one
  // JSON per user-scoped collection + scrubbed user record + README.
  // We trigger the download via a hidden anchor so the user gets the
  // browser's native save dialog with the right filename.
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await authAPI.exportMyData();
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Server sets Content-Disposition with the timestamped filename;
      // browsers honor it for blob downloads when we use the same path.
      a.download = `real-content-export.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('profile.exportDone') || 'Daten exportiert.');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('profile.exportFailed') || 'Export fehlgeschlagen.');
    } finally {
      setExporting(false);
    }
  };

  // DSGVO Art. 17. Soft-delete with 7-day grace. Two confirms because
  // this is destructive: a single-click misfire would lose everything,
  // and a single confirm dialog is too easy to dismiss.
  const handleDelete = async () => {
    const expected = t('profile.deleteConfirmWord') || 'LÖSCHEN';
    const typed = window.prompt(
      (t('profile.deletePrompt') || 'Tippe {{word}} um die Löschung zu bestätigen. Du hast danach 7 Tage Zeit, die Löschung per E-Mail rückgängig zu machen.').replace('{{word}}', expected)
    );
    if (typed !== expected) {
      if (typed !== null) {
        toast.info(t('profile.deleteCancelled') || 'Löschung abgebrochen.');
      }
      return;
    }
    setDeleting(true);
    try {
      await authAPI.deleteMyAccount();
      toast.success(t('profile.deleteScheduled') || 'Konto zur Löschung vorgemerkt. Du wirst jetzt ausgeloggt.');
      // Wait a beat so the toast is visible, then nuke local auth + redirect.
      setTimeout(() => {
        clearAuth();
        window.location.href = '/auth';
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('profile.deleteFailed') || 'Löschung fehlgeschlagen.');
      setDeleting(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await profileAPI.get();
      setProfile(response.data);
    } catch (error) {
      toast.error(t('profile.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await profileAPI.update({
        name: profile.name,
        phone_number: profile.phone_number,
        brand_name: profile.brand_name,
        profile_image: profile.profile_image,
        company_logo: profile.company_logo,
      });
      toast.success(t('profile.saved'));
    } catch (error) {
      toast.error(t('profile.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto">
        <Card className="glass border-[#e3dccb]">
          <CardHeader>
            <CardTitle className="text-3xl flex items-center gap-2">
              <User className="w-8 h-8 text-[#1f4d3a]" />
              {t('profile.title')}
            </CardTitle>
            <CardDescription>
              {t('profile.subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-[#23211a] mb-1.5 block">
                    {t('profile.name')} *
                  </label>
                  <Input
                    data-testid="profile-name-input"
                    type="text"
                    placeholder={t('onboarding.step1.namePlaceholder')}
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    required
                    className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a]"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-[#23211a] mb-1.5 block">
                    {t('profile.email')}
                  </label>
                  <Input
                    data-testid="profile-email-input"
                    type="email"
                    value={profile.email}
                    disabled
                    className="bg-[#f7f3ea]/85 border-[#e3dccb] opacity-60"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-[#23211a] mb-1.5 block">
                    {t('profile.phone')}
                  </label>
                  <Input
                    data-testid="profile-phone-input"
                    type="text"
                    placeholder={t('onboarding.step1.phonePlaceholder')}
                    value={profile.phone_number}
                    onChange={(e) => setProfile({ ...profile, phone_number: e.target.value })}
                    className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a]"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-[#23211a] mb-1.5 block">
                    {t('profile.brandName')}
                  </label>
                  <Input
                    data-testid="profile-brand-input"
                    type="text"
                    placeholder={t('onboarding.step1.brandPlaceholder')}
                    value={profile.brand_name}
                    onChange={(e) => setProfile({ ...profile, brand_name: e.target.value })}
                    className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a]"
                  />
                </div>
              </div>

              {/* Image Uploads */}
              <div className="border-t border-[#e3dccb] pt-8">
                <h3 className="text-lg font-semibold text-[#23211a] mb-4">
                  {t('profile.photo')} & {t('profile.logo')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <ImageUploadField
                    label={t('profile.photo')}
                    value={profile.profile_image}
                    onChange={(url) => setProfile({ ...profile, profile_image: url })}
                    uploadEndpoint="profile/upload/photo"
                    placeholder="https://example.com/your-photo.jpg"
                    previewShape="rounded"
                    testId="profile-photo"
                    cropToSquare
                  />

                  <ImageUploadField
                    label={t('profile.logo')}
                    value={profile.company_logo}
                    onChange={(url) => setProfile({ ...profile, company_logo: url })}
                    uploadEndpoint="profile/upload/logo"
                    placeholder="https://example.com/your-logo.png"
                    previewShape="square"
                    testId="company-logo"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  data-testid="save-profile-button"
                  type="submit"
                  className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white glow-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {saving ? t('common.loading') : t('common.save')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="border-[#d8d0bd]"
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* DSGVO controls. Sit below the profile editor as a separate
            card so they read as "rare but available", not part of the
            normal "save profile" flow. Export is non-destructive (left).
            Delete is destructive + requires confirmation typing (right). */}
        <Card className="glass border-[#e3dccb] mt-6">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-[#1e7a4f]" />
              {t('profile.dataAndAccountTitle') || 'Daten & Konto'}
            </CardTitle>
            <CardDescription>
              {t('profile.dataAndAccountSubtitle') ||
                'Lade alle deine Daten als ZIP herunter (DSGVO Art. 20) oder lösche dein Konto endgültig (DSGVO Art. 17).'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border border-[#e3dccb] bg-[#f2ecdf]">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#23211a]">
                  {t('profile.exportTitle') || 'Meine Daten herunterladen'}
                </p>
                <p className="text-xs text-[#6f6a5c] mt-1">
                  {t('profile.exportHelp') ||
                    'ZIP mit allen Datensätzen, die wir zu deinem Account speichern. API-Tokens und Passwörter sind ausgeschlossen.'}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleExport}
                disabled={exporting}
                className="border-[#d8d0bd] text-[#23211a] hover:bg-[#efe9db] w-full sm:w-auto sm:shrink-0"
                data-testid="export-data-btn"
              >
                {exporting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('common.loading') || 'Wird geladen…'}</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" /> {t('profile.exportButton') || 'ZIP herunterladen'}</>
                )}
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border border-[#b3543f]/40 bg-[#b3543f]/5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#a34430]">
                  {t('profile.deleteTitle') || 'Konto löschen'}
                </p>
                <p className="text-xs text-[#6f6a5c] mt-1">
                  {t('profile.deleteHelp') ||
                    'Markiert dein Konto zur Löschung. Du wirst ausgeloggt und 7 Tage später werden alle Daten endgültig entfernt. Schreib uns innerhalb der 7 Tage, um die Löschung rückgängig zu machen.'}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleDelete}
                disabled={deleting}
                className="border-[#b3543f]/50 text-[#b3543f] hover:bg-[#f7e8e3] w-full sm:w-auto sm:shrink-0"
                data-testid="delete-account-btn"
              >
                {deleting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('common.loading') || 'Wird gelöscht…'}</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-2" /> {t('profile.deleteButton') || 'Konto löschen'}</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
